import { useMemo } from 'react';
import { parsePhoneNumber, type CountryCode, type Examples, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

export const CONTROL_KEYS = [
  'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
  'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter', 'Escape',
] as const;

interface Options {
  phoneValue?: string;
  defaultCountry?: CountryCode;
}

interface Result {
  detectedCountry: CountryCode;
  maxDigits: number;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    externalHandler?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
  ) => void;
}

const getMaxDigitsForCountry = (country: CountryCode): number => {
  try {
    const ex = getExampleNumber(country, examples as Examples);
    if (ex) return ex.countryCallingCode.toString().length + ex.nationalNumber.toString().length;
  } catch { /* noop */ }
  return 15;
};

export const usePhoneMaxLength = ({ phoneValue, defaultCountry = 'UA' }: Options): Result => {
  const detectedCountry = useMemo((): CountryCode => {
    if (!phoneValue) return defaultCountry;
    try {
      return parsePhoneNumber(phoneValue)?.country ?? defaultCountry;
    } catch {
      return defaultCountry;
    }
  }, [phoneValue, defaultCountry]);

  const maxDigits = useMemo(() => getMaxDigitsForCountry(detectedCountry), [detectedCountry]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    externalHandler?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
  ) => {
    if (CONTROL_KEYS.includes(e.key as any) || e.ctrlKey || e.metaKey || e.altKey) {
      externalHandler?.(e);
      return;
    }
    if (/^\d$/.test(e.key)) {
      const digits = ((e.target as HTMLInputElement).value || '').replace(/[^\d]/g, '');
      if (digits.length >= maxDigits) {
        e.preventDefault();
        return;
      }
    }
    externalHandler?.(e);
  };

  return { detectedCountry, maxDigits, handleKeyDown };
};
